import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useRecommendations, useReferences } from '../hooks/useDiscovery';
import { RecommendationCard } from '../components/RecommendationCard';
import type { ReferenceWithCount } from '../types';

export function CategoryExplore() {
  const { id } = useParams<{ id: string }>();
  const { categories, loading: refsLoading } = useReferences();
  const [category, setCategory] = useState<ReferenceWithCount | null>(null);

  const {
    recommendations,
    loading,
    loadMoreLoading,
    error,
    hasMore,
    loadMore,
  } = useRecommendations({
    categoryId: id ?? undefined,
    limit: 12,
  });

  useEffect(() => {
    if (!refsLoading) {
      const found = categories.find((c) => c.id === id);
      setCategory(found ?? null);
    }
  }, [categories, refsLoading, id]);

  if (loading && !category) {
    return (
      <div className="min-h-screen">
        <header className="bg-white border-b border-gray-200">
          <div className="container py-4">
            <div className="skeleton h-8 w-48" />
          </div>
        </header>
        <main className="container py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="card">
                <div className="skeleton aspect-video w-full" />
                <div className="p-4 space-y-3">
                  <div className="skeleton h-6 w-3/4" />
                  <div className="skeleton h-4 w-full" />
                  <div className="skeleton h-4 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="container py-20 text-center">
        <svg className="mx-auto h-16 w-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h2 className="mt-4 text-xl font-semibold text-gray-900">Categoría no encontrada</h2>
        <p className="mt-2 text-gray-600">La categoría que buscas no existe.</p>
        <Link to="/" className="mt-6 inline-block btn-primary">
          Volver al inicio
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="bg-white border-b border-gray-200">
        <div className="container py-6">
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-900 mb-4 inline-block">
            ← Volver a explorar
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 capitalize">{category.name}</h1>
          <p className="mt-2 text-gray-500">
            {category.recommendationCount} recomendación{category.recommendationCount !== 1 ? 'es' : ''} en esta categoría
          </p>
        </div>
      </header>

      <main className="container py-12">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="card">
                <div className="skeleton aspect-video w-full" />
                <div className="p-4 space-y-3">
                  <div className="skeleton h-6 w-3/4" />
                  <div className="skeleton h-4 w-full" />
                  <div className="skeleton h-4 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && recommendations.length === 0 && (
          <div className="text-center py-20">
            <svg className="mx-auto h-16 w-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 className="mt-4 text-xl font-semibold text-gray-900">No hay recomendaciones</h3>
            <p className="mt-2 text-gray-600">No hay recomendaciones publicadas en esta categoría aún.</p>
          </div>
        )}

        {!loading && !error && recommendations.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {recommendations.map((rec) => (
                <RecommendationCard key={rec.id} recommendation={rec} />
              ))}
            </div>

            {hasMore && (
              <div className="mt-10 text-center">
                {loadMoreLoading ? (
                  <div className="flex justify-center gap-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="skeleton h-24 w-40 rounded-xl" />
                    ))}
                  </div>
                ) : (
                  <button type="button" onClick={loadMore} className="btn-secondary">
                    Cargar más
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}